// imageApi.js - Service for making API calls to the backend image endpoints

import { apiRequest } from './api';

/**
 * Upload a single image file
 * @param {File} file - The image file to upload
 * @returns {Promise<Object>} - Promise with the response containing image URLs
 */
const uploadImage = async (file) => {
  // Create a FormData object to send the file
  const formData = new FormData();
  formData.append('file', file);

  // Get the Firebase token from localStorage
  const token = localStorage.getItem('authToken');

  // Set headers for multipart/form-data
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Make the request
  try {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/images/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    // Handle 401 Unauthorized (token expired or invalid)
    if (response.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
      return null;
    }

    // Parse JSON response if the request was successful
    if (response.ok) {
      const text = await response.text();
      return text ? JSON.parse(text) : {};
    }

    // Handle error responses
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  } catch (error) {
    console.error('Image Upload Error:', error);
    throw error;
  }
};

/**
 * Upload multiple image files
 * @param {Array<File>} files - The image files to upload
 * @returns {Promise<Object>} - Promise with the response containing image URLs
 */
const uploadMultipleImages = async (files) => {
  // Create a FormData object to send the files
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });

  // Get the Firebase token from localStorage
  const token = localStorage.getItem('authToken');

  // Set headers for multipart/form-data
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Make the request
  try {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/images/upload-multiple`, {
      method: 'POST',
      headers,
      body: formData,
    });

    // Handle 401 Unauthorized (token expired or invalid)
    if (response.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
      return null;
    }

    // Parse JSON response if the request was successful
    if (response.ok) {
      const text = await response.text();
      return text ? JSON.parse(text) : {};
    }

    // Handle error responses
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  } catch (error) {
    console.error('Multiple Image Upload Error:', error);
    throw error;
  }
};

/**
 * Get the URL for an image with the specified size
 * @param {string} name - The unique name of the image
 * @param {string} size - The desired image size (small, medium, large)
 * @returns {Promise<Object>} - Promise with the response containing the image URL
 */
const getImageUrl = async (name, size) => {
  return apiRequest(`/images/url?name=${encodeURIComponent(name)}&size=${encodeURIComponent(size)}`);
};

/**
 * Upload a legal document file (PDF or HTML)
 * @param {File} file - The document file to upload
 * @returns {Promise<Object>} - Promise with { url, filename }
 */
const uploadDocument = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const token = localStorage.getItem('authToken');
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/documents/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
      return null;
    }

    if (response.ok) {
      const text = await response.text();
      return text ? JSON.parse(text) : {};
    }

    const errText = await response.text();
    throw new Error(errText || `API Error: ${response.status}`);
  } catch (error) {
    console.error('Document Upload Error:', error);
    throw error;
  }
};

// Export the API functions
export const imageApi = {
  uploadImage,
  uploadMultipleImages,
  getImageUrl,
  uploadDocument,
};

export default imageApi;